/**
 * Consolidated AI Review Status Types
 *
 * Single source of truth for AI review status related types.
 * All types are extracted from the generated api.d.ts to ensure type safety.
 *
 * @see poc-cms/src/utils/trackingConstants.ts for backend enum definition
 */

import type { components } from '@/services/clients/editorApi/_generated/api';
import type { QuizByIdResponse } from '@/services/httpServices/QuizServices/types';
import type { SequenceByIdResponse } from '@/services/httpServices/SequenceServices/types';

// =============================================================================
// Core AI Review Status Type - Extracted from api.d.ts
// =============================================================================

/**
 * AI Review Status values from the API.
 *
 * - `AI_PENDING`: Content was AI-generated and needs review before first publish
 * - `AI_APPROVED`: AI-generated content that has been reviewed/published
 * - `MANUAL`: Content was created manually by the user
 * - `null`: No AI status (legacy content or not applicable)
 *
 * @remarks
 * This type is extracted directly from the SequenceGetResponse schema
 * to ensure type safety with the API contract.
 */
export type AIReviewStatus = SequenceByIdResponse['data']['aiReviewStatus'];

/**
 * AI Review Status enum values as a const object for runtime comparisons.
 * Use this instead of magic strings for type-safe comparisons.
 *
 * @example
 * ```ts
 * if (aiReviewStatus === AI_REVIEW_STATUS.AI_PENDING) {
 *   // show warning
 * }
 * ```
 */
export const AI_REVIEW_STATUS = {
  AI_PENDING: 'AI_PENDING',
  AI_APPROVED: 'AI_APPROVED',
  MANUAL: 'MANUAL',
} as const;

/**
 * Type guard to check if a value is a valid AIReviewStatus
 */
export function isAIReviewStatus(value: unknown): value is AIReviewStatus {
  if (value === null) {
    return true;
  }
  return (
    value === AI_REVIEW_STATUS.AI_PENDING
    || value === AI_REVIEW_STATUS.AI_APPROVED
    || value === AI_REVIEW_STATUS.MANUAL
  );
}

// =============================================================================
// Unit Data Types - Extracted from api.d.ts for type-safe unit checking
// =============================================================================

/**
 * Sequence data shape for AI status checking.
 * Extracted from SequenceGetResponse for type safety.
 */
export type SequenceAIStatusData = Pick<
  SequenceByIdResponse['data'],
  'aiReviewStatus' | 'status' | 'lastPublishDate'
>;

/**
 * Quiz question shape for AI status checking.
 * Extracted from QuizDetail.questions for type safety.
 */
export type QuizQuestionAIStatusData = Pick<
  components['schemas']['QuizDetail']['questions'][number],
  'aiReviewStatus' | 'aiGeneratedFromSequence'
>;

/**
 * Quiz data shape for AI status checking.
 * Extracted from QuizGetResponse for type safety.
 */
export type QuizAIStatusData = Pick<
  QuizByIdResponse['data'],
  'status' | 'lastPublishDate'
> & {
  questions: QuizQuestionAIStatusData[];
};

/**
 * AI generated sequence reference structure.
 * Used to track which sequence a quiz question was generated from.
 */
export type AIGeneratedSequenceRef = components['schemas']['QuizDetail']['questions'][number]['aiGeneratedFromSequence'];

// =============================================================================
// Aggregated Status Result Types
// =============================================================================

/**
 * Result of AI status aggregation for a unit or collection of units.
 * Follows the same pattern as poc-cms getAIStatusFromUnit return type.
 */
export type AIStatusResult = {
  /** True if any content has AI_PENDING or AI_APPROVED status */
  isAIGenerated: boolean;
  /** True if any content has AI_PENDING status (needs review) */
  isAIGeneratedPending: boolean;
};

/**
 * Default result when no AI status data is available.
 */
export const DEFAULT_AI_STATUS_RESULT: AIStatusResult = {
  isAIGenerated: false,
  isAIGeneratedPending: false,
};
